import {
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@cibli/shared';
import { Mic, Volume2, Video, Settings } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import usePermissionsMediaDevices from '../hooks/usePermissionsMediaDevices';

interface MediaDevicesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MediaDevicesDialog({ open, onOpenChange }: MediaDevicesDialogProps) {
  const { devices, requestPermissions } = usePermissionsMediaDevices();
  const { t } = useTranslation();
  const getDeviceIcon = (kind: MediaDeviceKind) => {
    switch (kind) {
      case 'audioinput':
        return <Mic className="w-4 h-4" />;
      case 'audiooutput':
        return <Volume2 className="w-4 h-4" />;
      case 'videoinput':
        return <Video className="w-4 h-4" />;
      default:
        return <Settings className="w-4 h-4" />;
    }
  };

  const getDeviceTypeLabel = (kind: MediaDeviceKind) => {
    switch (kind) {
      case 'audioinput':
        return t('Settings.audioInput');
      case 'audiooutput':
        return t('Settings.audioOutput');
      case 'videoinput':
        return t('Settings.videoInput');
      default:
        return t('Settings.device');
    }
  };

  // Request permissions when dialog is opened
  useEffect(() => {
    if (open && !devices.length) {
      requestPermissions();
    }
  }, [open, requestPermissions, devices]);

  // Listen to device changes
  useEffect(() => {
    if (!open) return;
    navigator.mediaDevices.addEventListener('devicechange', requestPermissions);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', requestPermissions);
    };
  }, [open, requestPermissions]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('Settings.mediaDevices')}</DialogTitle>
          <DialogDescription>{t('Settings.mediaDevicesDescription')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {devices.map(device => (
            <div
              key={device.deviceId}
              className="flex items-center justify-between p-3 border rounded-lg"
            >
              <div className="flex items-center gap-3">
                {getDeviceIcon(device.kind)}
                <div>
                  <p className="font-medium">
                    {device.label ||
                      `${getDeviceTypeLabel(device.kind)} ${device.deviceId.slice(0, 8)}`}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    ID: {device.deviceId.slice(0, 16)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{getDeviceTypeLabel(device.kind)}</Badge>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
